import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Phone, ArrowRight, LogIn, AlertCircle, Search } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import eatzyLogo from "@/assets/eatzy-teal-disc-1024.png";

interface ManagerRestaurant {
  id: number;
  profile: string;
  name: string;
  business: number;
  address: string;
  phone?: string;
  timezone: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

interface AuthResponse {
  success: boolean;
  accessToken: string;
  roles?: {
    RestaurantManager?: string;
    LocalAreaManager?: string;
    RestaurantWebsite?: string;
  };
  error?: string;
}

function BrandedShell({ children, subtitle }: { children: React.ReactNode; subtitle?: string }) {
  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg overflow-hidden shadow-md">
        <div className="bg-eatzy-teal px-6 py-8 text-center">
          <div className="h-16 w-16 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-sm">
            <img
              src={eatzyLogo}
              alt="Eatzy"
              className="h-11 w-11 object-contain"
            />
          </div>
          <h1 className="text-eatzy-teal-foreground font-semibold text-lg tracking-wide">
            Partner Portal
          </h1>
          {subtitle && (
            <p className="text-eatzy-teal-foreground/75 text-sm mt-1">{subtitle}</p>
          )}
        </div>
        <div className="bg-card p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function PartnerLogin() {
  const [, navigate] = useLocation();
  const { login: mainAppLogin, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<ManagerRestaurant[] | null>(null);
  const [eatzyToken, setEatzyToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<AuthResponse['roles'] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const savedToken = sessionStorage.getItem('partnerEatzyToken');
    const savedRestaurantId = localStorage.getItem('partnerRestaurantId');
    
    if (savedToken && savedRestaurantId) {
      navigate('/partner');
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      const savedToken = sessionStorage.getItem('partnerEatzyToken');
      if (!savedToken) {
        setIsAutoAuthFetching(true);
        fetchPartnerToken();
      }
    }
  }, [isAuthenticated]);

  const [isAutoAuthFetching, setIsAutoAuthFetching] = useState(false);

  const fetchPartnerToken = async () => {
    try {
      const resp = await fetch('/api/partner/restmanager/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.accessToken) {
          sessionStorage.setItem('partnerEatzyToken', data.accessToken);
          setEatzyToken(data.accessToken);
          await fetchRestaurants(data.accessToken);
          return;
        }
      }
    } catch {
      // fall through to manual login
    } finally {
      setIsAutoAuthFetching(false);
    }
  };

  const fetchRestaurants = async (token: string) => {
    setIsLoadingRestaurants(true);
    try {
      const resp = await fetch('/api/partner/restmanager/restaurants', {
        headers: {
          'X-Eatzy-Token': token,
        },
      });
      if (!resp.ok) {
        sessionStorage.removeItem('partnerEatzyToken');
        setEatzyToken(null);
        return;
      }
      const data: ManagerRestaurant[] = await resp.json();
      if (data.length === 1) {
        selectRestaurant(data[0]);
        return;
      }
      setRestaurants(data);
    } catch {
      setError("Failed to load restaurants. Please try again.");
    } finally {
      setIsLoadingRestaurants(false);
    }
  };

  const handleAuthenticate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError(null);

    try {
      const resp = await fetch('/api/partner/restmanager/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data: AuthResponse = await resp.json();

      if (!data.success || !data.accessToken) {
        setError(data.error || "Invalid credentials. Please try again.");
        return;
      }

      setEatzyToken(data.accessToken);
      setRoles(data.roles ?? null);
      sessionStorage.setItem('partnerEatzyToken', data.accessToken);

      try {
        await mainAppLogin(email, password);
      } catch (loginErr) {
        console.warn('[PartnerLogin] Main app login failed (non-blocking):', loginErr);
      }

      await fetchRestaurants(data.accessToken);
    } catch {
      setError("An error occurred. Please check your connection and try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const selectRestaurant = (restaurant: ManagerRestaurant) => {
    localStorage.setItem('partnerRestaurantId', restaurant.id.toString());
    localStorage.setItem('partnerRestaurantName', restaurant.name);
    localStorage.setItem('partnerRestaurantProfile', restaurant.profile);
    navigate('/partner');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('partnerEatzyToken');
    localStorage.removeItem('partnerRestaurantId');
    localStorage.removeItem('partnerRestaurantName');
    localStorage.removeItem('partnerRestaurantProfile');
    localStorage.removeItem('partnerUserId');
    setEatzyToken(null);
    setRestaurants(null);
    setRoles(null);
  };

  if (isLoadingRestaurants || isAutoAuthFetching) {
    return (
      <BrandedShell subtitle="Loading your restaurants…">
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </BrandedShell>
    );
  }

  if (restaurants && restaurants.length > 1) {
    const q = searchQuery.trim().toLowerCase();
    const filteredRestaurants = q
      ? restaurants.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            (r.address && r.address.toLowerCase().includes(q))
        )
      : restaurants;

    return (
      <BrandedShell subtitle={`You have access to ${restaurants.length} restaurants`}>
        <div className="space-y-3">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search restaurants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
              data-testid="input-restaurant-search"
            />
          </div>

          {/* Scrollable restaurant list */}
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-0.5">
            {filteredRestaurants.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No restaurants found for &ldquo;{searchQuery}&rdquo;.
              </p>
            ) : (
              filteredRestaurants.map((restaurant) => (
                <button
                  key={restaurant.id}
                  onClick={() => selectRestaurant(restaurant)}
                  className="w-full p-4 rounded-md border bg-background hover-elevate text-left transition-colors"
                  data-testid={`restaurant-select-${restaurant.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{restaurant.name}</h3>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{restaurant.address}</span>
                      </div>
                      {restaurant.phone && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{restaurant.phone}</span>
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="pt-3 border-t">
            <Button variant="ghost" onClick={handleLogout} className="w-full">
              Sign out and use different credentials
            </Button>
          </div>
        </div>
      </BrandedShell>
    );
  }

  return (
    <BrandedShell subtitle="Sign in to manage your restaurant">
      <form onSubmit={handleAuthenticate} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="input-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="input-password"
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isAuthenticating}
          data-testid="button-sign-in"
        >
          {isAuthenticating ? (
            <>Signing in…</>
          ) : (
            <>
              <LogIn className="h-4 w-4 mr-2" />
              Sign In
            </>
          )}
        </Button>
      </form>

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Use your Eatzy restaurant manager credentials.
      </p>
    </BrandedShell>
  );
}
